import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Archive, Rocket, Filter } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PortfolioGrid from "@/components/PortfolioGrid";
import { projects, CATEGORIES } from "@/data/projects";

export default function PortfolioPage() {
  const [activeFilter, setActiveFilter] = useState("All");

  const counts = useMemo(() => {
    const map = { All: projects.length };
    CATEGORIES.filter((c) => c !== "All").forEach((c) => {
      map[c] = projects.filter((p) => p.categories?.includes(c)).length;
    });
    return map;
  }, []);

  const filtered = useMemo(() => {
    if (activeFilter === "All") return projects;
    return projects.filter((p) => p.categories?.includes(activeFilter));
  }, [activeFilter]);

  return (
    <div data-testid="portfolio-page">
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 hud-grid opacity-40 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-70 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 25% 30%, rgba(217,70,239,0.12), transparent 55%), radial-gradient(ellipse at 75% 70%, rgba(34,211,238,0.10), transparent 55%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Link
            to="/#portfolio"
            data-testid="portfolio-page-back"
            className="inline-flex items-center gap-2 font-hud text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft size={12} /> back_to_home
          </Link>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="overline">/ archive.full_index</div>
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-5 font-mono-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter leading-[0.95] text-slate-100"
              >
                <span className="block">The full</span>
                <span className="block">
                  <span className="glitch" data-text="mission">mission</span>{" "}
                  <span className="text-neon-fuchsia">log.</span>
                </span>
              </motion.h1>
              <p className="mt-6 max-w-2xl text-slate-300 font-body text-base sm:text-lg leading-relaxed">
                Every completed engagement across our studio history — sorted newest first.
                Governments, tourism boards, fashion labels, environmental NGOs, and a few
                things in between.
              </p>
            </div>

            <div className="lg:col-span-4 flex lg:justify-end">
              <div className="flex gap-3">
                <div className="p-4 bg-[#0a0a0a] border border-cyan-500/30 hud-corners">
                  <span className="hud-c-tl" />
                  <span className="hud-c-br" />
                  <div className="flex items-center gap-2 font-hud text-cyan-400/70">
                    <Archive size={12} /> total
                  </div>
                  <div className="mt-1 font-mono-display text-3xl md:text-4xl text-neon-cyan">
                    {projects.length}
                  </div>
                </div>
                <div className="p-4 bg-[#0a0a0a] border border-fuchsia-500/30 hud-corners">
                  <span className="hud-c-tl" />
                  <span className="hud-c-br" />
                  <div className="flex items-center gap-2 font-hud text-fuchsia-400/70">
                    <Rocket size={12} /> since
                  </div>
                  <div className="mt-1 font-mono-display text-3xl md:text-4xl text-neon-fuchsia">
                    2017
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full archive grid + filter chips */}
      <section className="relative py-12 md:py-16 border-t border-cyan-900/40">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          {/* Filter bar */}
          <div className="mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-2 font-hud text-cyan-400/80">
              <Filter size={12} /> filter.class
            </div>
            <div
              className="flex flex-wrap gap-2"
              role="tablist"
              aria-label="Portfolio category filter"
              data-testid="portfolio-filter-chips"
            >
              {CATEGORIES.map((cat) => {
                const isActive = activeFilter === cat;
                const count = counts[cat] ?? 0;
                return (
                  <button
                    key={cat}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveFilter(cat)}
                    data-testid={`portfolio-filter-${cat.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 font-mono-display uppercase tracking-widest text-xs border transition-[background-color,border-color,color,box-shadow] duration-200 ${
                      isActive
                        ? "bg-cyan-400 border-cyan-400 text-[#050505] shadow-[0_0_18px_rgba(34,211,238,0.45)]"
                        : "bg-transparent border-cyan-500/30 text-cyan-300 hover:border-cyan-400 hover:text-cyan-200 hover:bg-cyan-500/5"
                    }`}
                  >
                    <span>{cat}</span>
                    <span
                      className={`inline-flex items-center justify-center min-w-[1.4rem] h-[1.1rem] px-1 text-[0.65rem] font-mono-display ${
                        isActive ? "bg-[#050505]/25 text-[#050505]" : "bg-cyan-500/10 text-cyan-300/80"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Grid — animate on filter change */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
            >
              {filtered.length > 0 ? (
                <PortfolioGrid projects={filtered} />
              ) : (
                <div
                  data-testid="portfolio-empty-state"
                  className="p-10 border border-cyan-500/25 bg-[#0a0a0a] text-center hud-corners"
                >
                  <span className="hud-c-tl" />
                  <span className="hud-c-br" />
                  <div className="font-hud text-cyan-400/80">/ no_signal</div>
                  <p className="mt-3 font-mono-display text-lg text-slate-200 uppercase tracking-tight">
                    No missions match this class — yet.
                  </p>
                  <p className="mt-2 text-slate-400 font-body text-sm">
                    Try another filter, or reset to see the full archive.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveFilter("All")}
                    className="btn-ghost mt-5"
                    data-testid="portfolio-filter-reset"
                  >
                    Reset_filter
                  </button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-16 p-6 md:p-8 border border-cyan-500/30 bg-[#0a0a0a] flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="font-hud text-cyan-400/80">/ new_engagement</div>
              <div className="mt-2 font-mono-display text-xl md:text-2xl text-slate-100 uppercase tracking-tight">
                Ready to be the next mission?
              </div>
              <p className="mt-2 text-slate-400 font-body">
                Tell us what you want to ship. We usually reply within a business day.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/#contact" data-testid="portfolio-page-cta-contact" className="btn-neon">
                Start_Mission <ArrowRight size={13} />
              </Link>
              <Link to="/" data-testid="portfolio-page-cta-home" className="btn-ghost">
                Back_to_home
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
